import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  HypothesesHypothesisExternalLink as ExternalLink,
  HypothesesHypothesisExternalLinkCreate as ExternalLinkCreate,
  HypothesesHypothesisOnePage as OnePage,
  HypothesesHypothesisOnePageConclusion as OnePageConclusion,
} from '../generated/api/types.gen.ts';
import { hypothesesAddExternalLink, hypothesesExportOnePage, hypothesesList } from '../generated/api/sdk.gen.ts';
import { ApiFeil } from './feil.ts';
import { useApiKlient } from './klient.ts';
import { hypoteseNoekkel } from './useHypotheses.ts';

export type { ExternalLink, ExternalLinkCreate, OnePage, OnePageConclusion };

/**
 * H3.5 — eksterne lenker på hypotesen (P10, P11) og «Én side» (P8, P9).
 *
 * Lenkene kommer med hypotesen (`Hypothesis.externalLinks`) og har ingen egen lesing. «Legg til»
 * kalles straks; «Fjern» holdes tilbake i angre-vinduet som en overgang (`removeLink` i
 * `useHypoteseBehandling.ts`), så det finnes ingen fjern-hook her.
 *
 * «Én side» er et POST fordi det SKRIVER: hvert kall er én `hypothesis.exported` i loggen. Nøkkelen
 * står derfor utenfor prefikset `hypotheses` — en overgang som friskere opp rommet, skal aldri
 * eksportere på nytt — og spørringen hentes aldri av seg selv igjen (fokus, nett, tid).
 */
export const lenkeNoekkel = {
  enSide: (nr: number) => ['en-side', nr] as const,
};

function kast(res: { response?: Response; data?: unknown; error?: unknown }): never {
  throw ApiFeil.fra(res.response?.status ?? 0, res.error ?? res.data);
}

/** Legger til en lenke. Samme adresse to ganger gir lenken som finnes (200), ikke en ny. */
export function useLeggTilLenke() {
  const klient = useApiKlient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ hypoteseId, body }: { hypoteseId: string; nummer?: number; body: ExternalLinkCreate }): Promise<ExternalLink> => {
      const res = await hypothesesAddExternalLink({ client: klient, path: { id: hypoteseId }, body });
      if (!res.response?.ok || !res.data) kast(res);
      return res.data as ExternalLink;
    },
    // Etter en lenke, og når noen kom først (409 med taket nådd), hentes hypotesen på nytt, så lista og
    // låsen viser det som faktisk står. Uten nett hentes ingenting: detaljen står som den var.
    onSettled: (_l, e, { hypoteseId, nummer }) => {
      if (e && !(e instanceof ApiFeil && e.code === 'external_link_limit_reached')) return;
      void queryClient.invalidateQueries({ queryKey: hypoteseNoekkel.detalj(hypoteseId) });
      if (nummer !== undefined) void queryClient.invalidateQueries({ queryKey: hypoteseNoekkel.nummer(nummer) });
    },
  });
}

/**
 * Innholdet til «Én side», og eksporten ført i loggen — ett kall når siden åpnes (P8). Nummeret i
 * adressen slås opp til id-en med lista (en GET, som aldri skriver); hypotesen selv hentes ikke,
 * så notatene er aldri i nærheten av siden. Feiler kallet, er ingenting skrevet ut og ingenting ført.
 */
export function useEnSide(nr: number | null) {
  const klient = useApiKlient();
  return useQuery({
    queryKey: lenkeNoekkel.enSide(nr ?? 0),
    enabled: nr !== null,
    retry: false,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    queryFn: async (): Promise<OnePage | null> => {
      const liste = await hypothesesList({ client: klient, query: { number: String(nr) } });
      if (!liste.response?.ok || !liste.data) kast(liste);
      const treff = (liste.data as { items: Array<{ id: string }> }).items[0];
      if (!treff) return null;
      const res = await hypothesesExportOnePage({ client: klient, path: { id: treff.id } });
      if (!res.response?.ok || !res.data) kast(res);
      return res.data as OnePage;
    },
  });
}
